import { CalendarDays, Ban, TrendingDown, HelpCircle } from "lucide-react";
import type { DateFlex, FlexDayOption, FlexServiceFare } from "@/lib/types";

/**
 * B13 — "what would this cost a day either side?"
 *
 * Wade, 2026-08-24: *"I'd like to incorporate an after-booking fare search (same
 * day, day before (if possible based on itin — cruise cannot be a day earlier
 * due to cruise itin) / after) that shows … if we returned, same flight, next
 * day, we'd save $308 per person."*
 *
 * Three rules this panel exists to keep:
 *
 *  1. **Say WHICH flight.** "Same flight" and "a different flight" are different
 *    offers and are labelled differently. Conflating them is how the rebook box
 *    came to claim a $597 drop on a fare that had not moved (B11).
 *  2. **Say WHICH day.** Every row names the date and the weekday, because the
 *    entire proposition is that the traveller moves.
 *  3. **Never let silence mean "no saving".** A day we have not priced yet, and
 *    a day whose fare is too old to subtract, both SAY so. An empty row reads as
 *    "nothing there", which is a claim we have not earned.
 */

function money(n: number): string {
  return "$" + Math.round(Math.abs(n)).toLocaleString();
}

function weekday(iso: string): string {
  // Parse as a plain date; `new Date("2026-09-14")` is UTC midnight, which can
  // render as the 13th west of Greenwich.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "8:20 PM AA399, nonstop" — enough to know which departure is meant. */
function describe(fare: FlexServiceFare): string {
  const bits: string[] = [];
  if (fare.departLocal) bits.push(to12h(fare.departLocal));
  if (fare.airline) bits.push(fare.airline);
  if (fare.flightNumber) bits.push(fare.flightNumber);
  const stops =
    fare.stops == null
      ? null
      : fare.stops === 0
        ? "nonstop"
        : fare.stops + " stop" + (fare.stops === 1 ? "" : "s");
  return [bits.join(" ") || "a flight", stops].filter(Boolean).join(", ");
}

function to12h(hhmm: string): string {
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const ampm = h < 12 ? "AM" : "PM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return h + ":" + m[2] + " " + ampm;
}

const DAY_TAG: Record<FlexDayOption["label"], string> = {
  "day-before": "Day before",
  booked: "Your day",
  "day-after": "Day after",
};

function DayRow({ option, paid }: { option: FlexDayOption; paid: number | null }) {
  const isBooked = option.label === "booked";
  const blocked = !option.feasibility.allowed;

  // The same service on that day is the comparison that was asked for; a
  // different flight is offered only when the same one is not cheaper.
  const sameBetter =
    option.sameFlightSavings != null && option.sameFlightSavings > 0 ? option.sameFlight : null;
  const altBetter =
    !sameBetter && option.cheapestSavings != null && option.cheapestSavings > 0
      ? option.cheapest
      : null;

  return (
    <div
      className={
        "flex items-start gap-2 rounded-md px-2.5 py-2 text-xs " +
        (isBooked ? "bg-muted/40" : blocked ? "opacity-70" : "")
      }
    >
      <span className="mt-0.5 w-[76px] shrink-0 font-medium text-foreground">
        {DAY_TAG[option.label]}
      </span>
      <span className="mt-0.5 w-[110px] shrink-0 font-mono tabular-nums text-muted-foreground">
        {weekday(option.date)}
      </span>

      <span className="min-w-0 flex-1">
        {blocked ? (
          <span className="inline-flex items-start gap-1 text-muted-foreground">
            <Ban className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            {option.feasibility.reason}
          </span>
        ) : isBooked ? (
          <span className="text-muted-foreground">
            {option.sameFlight ? (
              <>
                {describe(option.sameFlight)} ·{" "}
                <span className="font-mono text-foreground">
                  {option.sameFlight.price != null ? money(option.sameFlight.price) : "—"}
                </span>{" "}
                now
              </>
            ) : (
              "The flight you hold."
            )}
          </span>
        ) : !option.covered ? (
          <span className="inline-flex items-start gap-1 text-muted-foreground">
            <HelpCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            We started watching this day — no fare yet.
          </span>
        ) : option.unknownReason && !sameBetter && !altBetter ? (
          <span className="inline-flex items-start gap-1 text-muted-foreground">
            <HelpCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            {option.unknownReason}
          </span>
        ) : sameBetter ? (
          <span className="text-emerald-800">
            <span className="font-medium">Same flight</span> — {describe(sameBetter)} ·{" "}
            <span className="font-mono">{money(sameBetter.price as number)}</span>, saving{" "}
            <span className="font-mono font-semibold">
              {money(option.sameFlightSavings as number)}
            </span>{" "}
            per person
          </span>
        ) : altBetter ? (
          <span className="text-sky-800">
            <span className="font-medium">A different flight</span> — {describe(altBetter)} ·{" "}
            <span className="font-mono">{money(altBetter.price as number)}</span>, saving{" "}
            <span className="font-mono font-semibold">
              {money(option.cheapestSavings as number)}
            </span>{" "}
            per person
          </span>
        ) : (
          <span className="text-muted-foreground">
            {option.sameFlight?.price != null && paid != null ? (
              <>
                Same flight is <span className="font-mono">{money(option.sameFlight.price)}</span> —{" "}
                {money(option.sameFlight.price - paid)} more than you paid.
              </>
            ) : (
              "Nothing cheaper that day."
            )}
          </span>
        )}
      </span>
    </div>
  );
}

export default function DateFlexPanel({ flex }: { flex: DateFlex | null | undefined }) {
  if (!flex || flex.options.length === 0) return null;
  const paid = flex.pricePaidPerPerson;
  const best = flex.best;

  return (
    <section className="mt-2 rounded-lg border bg-card px-3 py-2.5">
      <div className="flex items-center gap-1.5">
        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <h4 className="text-xs font-semibold text-foreground">Moving a day</h4>
        {paid != null && (
          <span className="text-[11px] text-muted-foreground">
            against the <span className="font-mono">{money(paid)}</span> per person you paid
          </span>
        )}
      </div>

      {/* The headline, only when a move is both possible and worth it. */}
      {best && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2">
          <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
          <p className="text-xs text-emerald-900">
            {best.sameFlight ? (
              <>
                <span className="font-medium">The same flight</span> on {weekday(best.date)} is{" "}
                <span className="font-mono font-semibold">{money(best.savingsPerPerson)}</span>{" "}
                cheaper per person
              </>
            ) : (
              <>
                <span className="font-medium">A different flight</span> on {weekday(best.date)} —{" "}
                {describe(best.fare)} — is{" "}
                <span className="font-mono font-semibold">{money(best.savingsPerPerson)}</span>{" "}
                cheaper per person
              </>
            )}
            {flex.passengers > 1 && (
              <>
                , <span className="font-mono font-semibold">{money(best.savingsParty)}</span> for
                the {flex.passengers} of you
              </>
            )}
            . Changing the date is your call with the airline — we only watch.
          </p>
        </div>
      )}

      <div className="mt-1.5 space-y-0.5">
        {flex.options.map((o) => (
          <DayRow key={o.date} option={o} paid={paid} />
        ))}
      </div>
    </section>
  );
}
